import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  isNilOrZero,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import Tag from 'core/components/Tag';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import EnrollProgramsStep from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/EnrollProgramsStep';
import {
  EnrollStep,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/steps';
import useEnrollFlow from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/useEnrollFlow';
import {
  PCCEnrollParams,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/types';
import RTMAttestation from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/RTM/RTMAttestation';
import RTMRecommendation from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/RTM/RTMRecommendation';
import {
  pccEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import clsx from 'clsx';

type TitleProps = {
  step: EnrollStep;
};

function EnrollDialogTitle({ step }: TitleProps) {
  return (
    <>
      {step === 'programs' && (
        <span className="text-lg font-semibold mb-4 block">Enroll to Programs</span>
      )}
      {step === 'recommendation' && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-lg font-semibold">Recommend Care Progress / RTM</span>
          <Tag tagName="May be eligible" defaultColor="!bg-sky-100 !text-sky-800 font-medium" />
        </div>
      )}
      {step === 'attestation' && (
        <span className="text-lg font-semibold mb-4 block">Attest RTM Medical Necessity</span>
      )}
    </>
  );
}

export default function PCCEnrollDialog() {
  const [context, setContext] = useState<PCCEnrollParams | null>(null);
  const [canApproveRtm, setCanApproveRtm] = useState(false);
  const {
    step,
    selectedPrograms,
    planText,
    encounterValue,
    isEnrolling,
    setSelectedPrograms,
    setPlanText,
    setEncounterValue,
    goBack,
    continueFromPrograms,
    approvePlan,
    completeAttestation,
    skip,
  } = useEnrollFlow(context);

  useEffect(
    () => pccEventChannel.on('onShowPCCEnroll', setContext),
    [],
  );

  useEffect(() => {
    if (step !== 'recommendation') {
      setCanApproveRtm(false);
    }
  }, [step]);

  const closeDialog = useCallback(() => {
    setContext(null);
  }, []);

  const handleSkip = useCallback(() => {
    if (!isEnrolling) {
      skip();
      closeDialog();
    }
  }, [closeDialog, isEnrolling, skip]);

  const handleProgramsContinue = useCallback(async () => {
    try {
      const isDone = await continueFromPrograms();
      if (isDone) {
        closeDialog();
      }
    } catch {
      // Global mutation onError handles error notify
    }
  }, [closeDialog, continueFromPrograms]);

  const handleApproveRtm = useCallback(() => {
    approvePlan();
  }, [approvePlan]);

  const handleAttest = useCallback(async () => {
    try {
      await completeAttestation();
      closeDialog();
    } catch {
      // Global mutation onError handles error notify
    }
  }, [closeDialog, completeAttestation]);

  const isEncounterSelectionEditable = !isNil(context)
    && isNilOrZero(context.encounterId);

  return !isNil(context) ? (
    <Dialog
      open
      onClose={handleSkip}
      title={<EnrollDialogTitle step={step} />}
      disableBackdropClick
      className={clsx('!space-y-0 sm:w-[560px] md:w-[640px] min-h-[320px]', {
        '!w-[420px] !min-h-[150px]': step === 'programs',
      })}
    >
      <div className="flex flex-col gap-4 flex-1">
        {step === 'programs' && (
          <EnrollProgramsStep
            programs={context.programs}
            selectedPrograms={selectedPrograms}
            isEnrolling={isEnrolling}
            onChange={setSelectedPrograms}
          />
        )}
        {step === 'recommendation' && (
          <RTMRecommendation
            patientId={context.patientId}
            visitNoteId={context.visitNoteId}
            planText={planText}
            onPlanTextChange={setPlanText}
            onCanApproveChange={setCanApproveRtm}
            isEncounterSelectionEditable={isEncounterSelectionEditable}
            encounterValue={encounterValue}
            onEncounterValueChange={setEncounterValue}
          />
        )}
        {step === 'attestation' && (
          <RTMAttestation />
        )}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-4">
            <Button
              variant="outlined"
              type="button"
              disabled={isEnrolling}
              onClick={handleSkip}
              className="!max-w-[unset] !min-w-[unset]"
            >
              Skip
            </Button>
            {step === 'attestation' && (
              <Button
                variant="outlined"
                type="button"
                disabled={isEnrolling}
                onClick={goBack}
                className="!max-w-[unset] !min-w-[unset]"
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-4">
            {step === 'programs' && (
              <Button
                variant="contained"
                color="primary"
                type="button"
                disabled={isEnrolling || selectedPrograms.length === 0}
                onClick={handleProgramsContinue}
                className="!max-w-[unset] !min-w-[unset]"
              >
                {isEnrolling ? (
                  <span className="flex items-center gap-2">
                    <Spinner size={16} />
                    Enrolling...
                  </span>
                ) : 'Continue'}
              </Button>
            )}
            {step === 'recommendation' && (
              <Button
                variant="contained"
                color="primary"
                type="button"
                disabled={!canApproveRtm}
                onClick={handleApproveRtm}
                className="!max-w-[unset] !min-w-[unset]"
              >
                Approve RTM
              </Button>
            )}
            {step === 'attestation' && (
              <Button
                variant="contained"
                color="primary"
                type="button"
                disabled={isEnrolling}
                onClick={handleAttest}
                className="!max-w-[unset] !min-w-[unset]"
              >
                {isEnrolling ? (
                  <span className="flex items-center gap-2">
                    <Spinner size={16} />
                    Enrolling...
                  </span>
                ) : 'Attest & enroll'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  ) : null;
}
